import './accwriter.css'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { Translates } from '@/services/repository/i18n'
import { BackButton } from '@/components/layout/NavBar'
import { Spinner } from '@/components/spinner'
import { QRScanner } from '@/components/qr/scanner'
import { useAppContext } from '@/context/app'

export function isNFCSupported(): boolean {
  return 'NDEFReader' in window
}

export interface AccountantNFCWriterProps {
  onRead: (cardID: string) => void
  onReadError: () => void
  onInit: (success: boolean) => void
}

export const readNFC = (props: AccountantNFCWriterProps) => {
  if (!('NDEFReader' in window)) {
    props.onInit(false)
    return () => { }
  }

  try {
    const ndef = new NDEFReader()
    const abortController = new AbortController()
    let aborted = false

    const onReadingHandler = (event: any) => {
      if (aborted) return

      const { serialNumber } = event as NDEFMessageEvent

      if (!serialNumber) {
        props.onReadError()
        return
      }

      const ucid = serialNumber.replaceAll(':', '').toUpperCase()
      props.onRead(ucid)
    }

    ndef.scan({ signal: abortController.signal }).then(
      () => {
        ndef.addEventListener('reading', onReadingHandler)
        ndef.addEventListener('readingerror', props.onReadError)
        props.onInit(true)
      },
      () => {
        props.onInit(false)
      }
    )

    return () => {
      aborted = true
      abortController.abort()
      ndef.removeEventListener('reading', onReadingHandler)
      ndef.removeEventListener('readingerror', props.onReadError)
    }
  } catch {
    props.onInit(false)
    return () => { }
  }
}

export interface AccountantNFCWriterPopupProps {
  onRead: (cardID: string) => void
  onClose: () => void
  translates: Translates
}

export enum AccountantNFCWriterPopupStatuses {
  error,
  unsupported,
  ready,
  readed,
}

export const AccountantNFCWriterPopup = (props: AccountantNFCWriterPopupProps) => {
  const { translates } = props
  const baseKey = 'elements.nfcwriter'
  const t = (k: string) => translates.for(`${baseKey}.${k}`)

  const [cardId, setCardId] = useState<string>()
  const [status, setStatus] = useState<AccountantNFCWriterPopupStatuses>()

  useEffect(() => {
    return readNFC({
      onInit: (success: boolean) => {
        setStatus(
          success
            ? AccountantNFCWriterPopupStatuses.ready
            : AccountantNFCWriterPopupStatuses.unsupported
        )
      },
      onReadError: () => {
        setStatus(AccountantNFCWriterPopupStatuses.error)
      },
      onRead: (newCardId) => {
        setStatus(AccountantNFCWriterPopupStatuses.readed)
        setCardId(newCardId)
        props.onRead(newCardId)
        props.onClose()
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (status === AccountantNFCWriterPopupStatuses.error) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">{t('status.error')}</div>
      </div>
    )
  }

  if (status === AccountantNFCWriterPopupStatuses.readed) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">
          {t('status.readed').replace('{{card_id}}', cardId ?? '')}
        </div>
      </div>
    )
  }

  if (status === AccountantNFCWriterPopupStatuses.ready) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">{t('status.insert_nfc_card')}</div>
      </div>
    )
  }

  if (status === AccountantNFCWriterPopupStatuses.unsupported) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">{t('status.unsupported')}</div>
      </div>
    )
  }

  return (
    <div className="nfcwriter-popup">
      <Spinner />
    </div>
  )
}

const URI_PREFIXES = [
  '',
  'http://www.',
  'https://www.',
  'http://',
  'https://',
  'tel:',
  'mailto:',
  'ftp://anonymous:anonymous@',
  'ftp://ftp.',
  'ftps://',
  'sftp://',
  'smb://',
  'nfs://',
  'ftp://',
  'dav://',
  'news:',
  'telnet://',
  'imap:',
  'rtsp://',
  'urn:',
  'pop:',
  'sip:',
  'sips:',
  'tftp:',
  'btspp://',
  'btl2cap://',
  'btgoep://',
  'tcpobex://',
  'irdaobex://',
  'file://',
  'urn:epc:id:',
  'urn:epc:tag:',
  'urn:epc:pat:',
  'urn:epc:raw:',
  'urn:epc:',
  'urn:nfc:',
] as const

function encodeUriPayload(url: string): Uint8Array {
  const value = url.trim()

  let bestCode = 0
  let bestPrefix = ''

  for (let code = 1; code < URI_PREFIXES.length; code++) {
    const prefix = URI_PREFIXES[code] ?? ''
    if (!prefix) continue
    if (value.startsWith(prefix) && prefix.length > bestPrefix.length) {
      bestPrefix = prefix
      bestCode = code
    }
  }

  const rest = value.slice(bestPrefix.length)
  const restBytes = new TextEncoder().encode(rest)
  const out = new Uint8Array(1 + restBytes.length)
  out[0] = bestCode
  out.set(restBytes, 1)
  return out
}

function normalizeUrlCandidate(rawValue: string): string | null {
  const trimmed = rawValue.trim()
  if (!trimmed) {
    return null
  }

  try {
    if (typeof window !== 'undefined' && trimmed.startsWith('/')) {
      return new URL(trimmed, window.location.origin).toString()
    }
    return new URL(trimmed).toString()
  } catch {
    return null
  }
}

export const AccountantNFCWriterPage = () => {
  const location = useLocation()
  const { services, session } = useAppContext()

  const [targetUrl, setTargetUrl] = useState('')
  const [scannedValue, setScannedValue] = useState('')
  const [scanError, setScanError] = useState<string | null>(null)
  const [statusKey, setStatusKey] = useState<string | null>(null)

  const isWritingRef = useRef(false)

  const queryUrl =
    new URLSearchParams(location.search).get('url')?.trim() ?? ''

  useEffect(() => {
    if (!queryUrl) {
      return
    }

    const normalizedUrl = normalizeUrlCandidate(queryUrl)
    setScannedValue(queryUrl)

    if (!normalizedUrl) {
      setTargetUrl('')
      setScanError('Parametr "url" nie zawiera poprawnego linku.')
      return
    }

    setTargetUrl(normalizedUrl)
    setScanError(null)
  }, [queryUrl])

  const hasQueryUrl = !!queryUrl

  const scannerHelp = useMemo(() => {
    if (hasQueryUrl) {
      return 'Uzywam linku przekazanego w parametrze URL.'
    }
    if (targetUrl) {
      return 'Kod QR zostal odczytany. Mozesz teraz zapisac ten link na tagu NFC.'
    }
    return 'Zeskanuj kod QR zawierajacy link, a potem przyloz tag NFC do zapisu.'
  }, [hasQueryUrl, targetUrl])

  async function handleWriteClick() {
    if (!session) return

    if (!isNFCSupported()) {
      setStatusKey('status.unsupported')
      return
    }
    if (!targetUrl) {
      setScanError('Najpierw zeskanuj kod QR z linkiem albo podaj ?url=...')
      return
    }
    if (isWritingRef.current) return
    isWritingRef.current = true

    try {
      setStatusKey('status.writing')

      const writer = new NDEFReader()
      const urlRecordBinary: NDEFRecordInit = {
        recordType: 'url',
        data: encodeUriPayload(targetUrl),
      }
      const urlRecordString: NDEFRecordInit = {
        recordType: 'url',
        data: targetUrl,
      }

      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await (writer as any).write(
            { records: [urlRecordBinary] },
            { overwrite: true }
          )
          setStatusKey('status.write_ok')
          setScanError(null)
          return
        } catch (binaryError) {
          try {
            await (writer as any).write(
              { records: [urlRecordString] },
              { overwrite: true }
            )
            setStatusKey('status.write_ok')
            setScanError(null)
            return
          } catch {
            if (attempt === 3) throw binaryError
            await new Promise((resolve) => setTimeout(resolve, 200))
          }
        }
      }
    } catch (error) {
      console.error('Write failed:', error)
      setStatusKey('status.write_error')
    } finally {
      isWritingRef.current = false
    }
  }

  if (!session) {
    return <Spinner />
  }

  const baseKey = 'elements.nfcwriter'
  const t = (k: string) => session.translates.for(`${baseKey}.${k}`)

  return (
    <LoggedInPageLayout
      pageName="nfcwriter"
      services={services}
      session={session}
      navbarLeft={
        <BackButton services={services} translates={session.translates} />
      }
    >
      <div className="nfcwriter-rows">
        <div className="nfcwriter-row nfcwriter-row--status">
          {scannerHelp}
        </div>

        {!hasQueryUrl && !targetUrl && (
          <div className="nfcwriter-row nfcwriter-row--scanner">
            <div className="nfcwriter-label">Skaner QR</div>
            <QRScanner
              services={services}
              onScan={(data: string) => {
                const normalizedUrl = normalizeUrlCandidate(data)
                setScannedValue(data)
                if (!normalizedUrl) {
                  setTargetUrl('')
                  setScanError('Zeskanowany kod QR nie zawiera poprawnego linku.')
                  return
                }
                setTargetUrl(normalizedUrl)
                setScanError(null)
              }}
            />
          </div>
        )}

        <div className="nfcwriter-row nfcwriter-row--url">
          <div className="nfcwriter-label">Odczytana zawartosc QR</div>
          <div className="nfcwriter-value nfcwriter-value--mono">
            {scannedValue || '-'}
          </div>
        </div>

        <div className="nfcwriter-row nfcwriter-row--url">
          <div className="nfcwriter-label">URL do zapisu</div>
          <input className="nfcwriter-input" value={targetUrl} readOnly />
        </div>

        {scanError && (
          <div className="nfcwriter-row nfcwriter-row--status">
            {scanError}
          </div>
        )}

        {statusKey && (
          <div className="nfcwriter-row nfcwriter-row--status">
            {t(statusKey)}
          </div>
        )}

        <div className="nfcwriter-row nfcwriter-row--actions">
          {!hasQueryUrl && (
            <button
              className="nfcwriter-btn"
              onClick={() => {
                setTargetUrl('')
                setScannedValue('')
                setScanError(null)
                setStatusKey(null)
              }}
            >
              Skanuj ponownie
            </button>
          )}

          <button
            className="nfcwriter-btn"
            onClick={handleWriteClick}
            disabled={!targetUrl}
          >
            {t('buttons.write')}
          </button>
        </div>
      </div>
    </LoggedInPageLayout>
  )
}
